import { getTimeframeSeconds } from '../../utils/chartUtils'

/**
 * The walkthrough transport's pacing vocabulary: a DETAIL level, and a PACE within it.
 *
 * The Backtest tab's bar picks a literal engine speed (0.5x … 50x). A walkthrough is watched
 * rather than scanned, so its transport speaks in two terms instead:
 *
 *   DETAIL — how finely the run is drawn. Candle reveals whole bars; Tick builds each bar from
 *            its own sub-bar data as it forms.
 *   PACE   — how fast, and it means something DIFFERENT in each detail, which is the one thing
 *            to hold on to here:
 *              · under Candle it is a feel, mapped to one of the engine's speed brackets;
 *              · under Tick it is a literal multiple of the market clock. 1x IS real time —
 *                one bar takes one timeframe — and 10x is ten times that.
 *
 * Two rules rather than three. There used to be a third detail, Real-time, which disabled the
 * pace control because its speed came from the clock rather than the speed table; it was never
 * a granularity, only Tick at 1x, and it says so now.
 *
 * The pair maps onto one number the engine understands (`engineSpeed`) plus, for tick, a bar
 * interval the engine's own timing table cannot express (`barIntervalMs`). Keeping the mapping
 * here rather than in the bar or the engine is what lets the UI talk about feel while
 * `usePlayback` keeps its single `speed` input.
 */
export type ReplayDetail = 'candle' | 'tick'

/** Relative paces in candle detail: slow → fast. */
export const CANDLE_PACES: readonly number[] = [1, 2, 3]

/**
 * Tick paces, as MULTIPLES OF REAL TIME. 1 is real time itself.
 *
 * So on an M1 chart a bar takes 60s at 1x and 6s at 10x; on H1, an hour at 1x and six minutes
 * at 10x. Deliberately literal — the label is checkable against a clock — which also means tick
 * is for studying how a handful of bars form, not for watching a long run through. Candle is
 * what moves a run.
 */
export const TICK_PACES: readonly number[] = [1, 2, 3, 5, 10]

/** The pace tick always opens at. Never 1x: that would hand the user a bar per timeframe. */
export const DEFAULT_TICK_PACE = 2

/** Every run opens at the medium baseline, bar by bar. */
export const DEFAULT_DETAIL: ReplayDetail = 'candle'
export const DEFAULT_PACE = 2

/**
 * Candle pace → the engine speed that produces it.
 *
 * The targets are `usePlayback.getPlayTiming`'s brackets, which is why these are the numbers
 * they are: 2 → 250ms per bar, 5 → 100ms × 1 bar, 10 → 100ms × 2 bars. Candle spends its whole
 * range on reveal rate, because whole-bar reveal is the only thing it does.
 */
const CANDLE_PACE_TO_SPEED: Record<number, number> = { 1: 2, 2: 5, 3: 10 }

/**
 * The engine speed tick reports, at every pace.
 *
 * Cosmetic, and it has to be: tick's timing comes from `barIntervalMs`, and `usePlayback`
 * returns that override before `getPlayTiming` is ever consulted. It still has to be a real
 * member of AVAILABLE_SPEEDS, because `useFormingBar` runs a set-membership test on it — the
 * slowest bracket is the honest answer for a mode that dwells on every bar.
 */
const TICK_ENGINE_SPEED = 0.5

/** The paces this detail offers. */
export function pacesFor(detail: ReplayDetail): readonly number[] {
  return detail === 'tick' ? TICK_PACES : CANDLE_PACES
}

/** How a pace is named in the transport. Tick's 1x is real time, and says so. */
export function paceLabel(detail: ReplayDetail, pace: number): string {
  return detail === 'tick' && pace === 1 ? 'Real-time' : `${pace}x`
}

/** The engine speed for a (detail, pace) pair. */
export function engineSpeed(detail: ReplayDetail, pace: number): number {
  if (detail === 'tick') return TICK_ENGINE_SPEED
  return CANDLE_PACE_TO_SPEED[pace] ?? CANDLE_PACE_TO_SPEED[DEFAULT_PACE]
}

/**
 * A pace that is legal in this detail.
 *
 * Guards the pace control itself. The detail SWITCH goes through `paceForDetail` instead,
 * which is a different question — see there.
 */
export function clampPace(detail: ReplayDetail, pace: number): number {
  const paces = pacesFor(detail)
  if (paces.includes(pace)) return pace
  return detail === 'tick' ? DEFAULT_TICK_PACE : DEFAULT_PACE
}

/**
 * The pace to land on when the DETAIL changes.
 *
 * Not a clamp, and not symmetric. Entering tick always lands on 2x even though 1x and 3x are
 * legal there, because tick's paces are wall-clock multiples: carrying a 1x across from candle
 * would silently hand the user a bar per timeframe — a minute of nothing on M1, an hour on H1.
 * Leaving tick keeps the pace when candle has it, so 2x → 2x reads as continuity, and drops the
 * faster ones to candle's own ceiling.
 */
export function paceForDetail(next: ReplayDetail, prev: number): number {
  if (next === 'tick') return DEFAULT_TICK_PACE
  return CANDLE_PACES.includes(prev) ? prev : CANDLE_PACES[CANDLE_PACES.length - 1]
}

/**
 * Wall-clock milliseconds one bar should take, or null to let the engine's speed table decide.
 *
 * Tick is LITERAL: `pace` is a multiple of the market clock, so an M1 chart at 1x forms a
 * candle once a real minute, exactly like watching the live market, and at 10x once every six
 * seconds. Tied to the run's timeframe so it scales correctly (M15 = one per 15 minutes at 1x).
 *
 * This value is also what tells the rest of the engine that a bar's duration is wall-clock
 * derived: `useFormingBar` reads a non-null override as "this detail animates", sizes its
 * snapshot ring from it, and lifts its tick-density ceiling to H1 on the strength of it.
 */
export function barIntervalMs(
  detail: ReplayDetail,
  timeframe: string,
  pace: number,
): number | null {
  if (detail !== 'tick') return null
  const multiple = TICK_PACES.includes(pace) ? pace : DEFAULT_TICK_PACE
  return Math.round((getTimeframeSeconds(timeframe) * 1000) / multiple)
}

/**
 * Whether the bar should be drawn as it forms.
 *
 * The detail decides it outright, and the pace has no say any more — which is new. It used to,
 * because tick's fast paces rode the engine's speed table into a region where the play loop
 * reveals several bars per redraw and a forming bar would be painted only to be skipped. Tick
 * paces are wall-clock multiples now, so `usePlayback` pins the batch at one bar and the
 * shortest bar tick can ask for is M1 at 10x — six seconds, which is sixty animation steps.
 */
export function buildsFormingBar(detail: ReplayDetail): boolean {
  return detail === 'tick'
}
